import clsx from 'clsx';
import React from 'react';
import styles from './EmailSuggestionButton.module.css';

export interface EmailSuggestionButtonProps {
  /** The corrected address to offer, e.g. `ada@gmail.com`. */
  suggestedEmail: string;
  /**
   * Applies the correction. The ONLY thing allowed to change the email on the
   * user's behalf [GRO-6429] — a suggestion is never applied automatically, so
   * each surface keeps its own handler and its own resubmit path.
   */
  onAccept: () => void;
  /** Placement for this surface: spacing or flex alignment, never appearance. */
  className?: string;
}

/**
 * The did-you-mean correction offered after an email-domain rejection.
 *
 * Presentational on purpose. It renders the affordance and nothing else — no
 * state, no fetch, no knowledge of which submit path follows. That is what lets
 * three surfaces with three different state models (a jotai atom, widget state,
 * and local `useState`) share one control: they keep their accept handlers, which
 * differ in real ways, and hand over only what the button needs to look and
 * behave identically everywhere.
 *
 * A real `<button type="button">`, not a styled link or a div: it performs an
 * action rather than navigating, and it must be reachable and operable by keyboard
 * without any extra handlers. `type="button"` is load-bearing — inside these forms
 * a default-type button would submit on click.
 */
export function EmailSuggestionButton({
  suggestedEmail,
  onAccept,
  className,
}: EmailSuggestionButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      className={clsx(styles.suggestion, className)}
      onClick={onAccept}
      // Spelled out for a screen reader, because "Did you mean …?" alone does not
      // say what activating it does.
      aria-label={`Use suggested email address ${suggestedEmail}`}
    >
      Did you mean {suggestedEmail}?
    </button>
  );
}
